<script setup>
import { Badge } from '@dashhudson/dashing-ui/badge';
import { Column, DataTable } from '@dashhudson/dashing-ui/data-table';
import { ProgressBar } from '@dashhudson/dashing-ui/progress-bar';
import { Section } from '@dashhudson/dashing-ui/section';
import { SectionHeader } from '@dashhudson/dashing-ui/section-header';
import { isEmpty } from 'lodash-es';
import isNumber from 'lodash/isNumber';
import orderBy from 'lodash/orderBy';
import { computed, ref } from 'vue';

import {
  NO_DATA_FOUND,
  NO_DATA_FOUND_FOR_FILTERS,
  TOPIC_COMPARISON_MAX_TOPIC_COUNT,
  TOPIC_COMPARISONS_TOOLTIPS,
} from '@/app/socialListening/constants';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { formatMetric } from '@/utils/formatters';

const comparisonStore = useSocialListeningComparisonsStore();

const sortField = ref();
const sortOrder = ref();

function roundToHundred(numbers) {
  if (!Array.isArray(numbers) || numbers.length === 0) return [];
  const rounded = numbers.map(Math.round);
  const diff = 100 - rounded.reduce((a, b) => a + b, 0);

  if (diff !== 0) {
    if (diff > 0) {
      const minIndex = rounded.indexOf(Math.min(...rounded));
      rounded[minIndex] += diff;
    } else {
      const maxIndex = rounded.indexOf(Math.max(...rounded));
      rounded[maxIndex] += diff;
    }
  }

  return rounded;
}

const summaryStats = computed(() => {
  const summaryItems = [];
  let totalMentions = 0;
  Object.keys(comparisonStore.topicsStats).forEach((key) => {
    totalMentions += comparisonStore.topicsStats[key]?.totalPosts ?? 0;
    summaryItems.push({
      topic: key,
      mentionsValue: comparisonStore.topicsStats[key]?.totalPosts ?? 0,
      mentionsLabel: formatMetric({
        value: comparisonStore.topicsStats[key]?.totalPosts,
        format: 'number_with_commas',
      }),
      engagementsValue: comparisonStore.topicsStats[key]?.totalEngagements ?? 0,
      engagementsLabel: formatMetric({
        value: comparisonStore.topicsStats[key]?.totalEngagements,
        format: 'number_with_commas',
      }),
      impressionsValue: comparisonStore.topicsStats[key]?.totalImpressions ?? 0,
      impressionsLabel: formatMetric({
        value: comparisonStore.topicsStats[key]?.totalImpressions,
        format: 'number_with_commas',
      }),
      netSentimentValue: comparisonStore.topicsStats[key]?.netSentiment ?? null,
      netSentimentLabel: comparisonStore.topicsStats[key]?.netSentiment ?? '-',
    });
  });

  if (totalMentions === 0) {
    summaryItems.forEach((item) => {
      item.shareOfVoiceValue = null;
      item.shareOfVoiceLabel = '-';
    });
    return summaryItems;
  }

  const percentages = summaryItems.map((item) => (item.mentionsValue / totalMentions) * 100);
  const formattedPercentages = roundToHundred(percentages);

  summaryItems.forEach((item, index) => {
    item.shareOfVoiceValue = formattedPercentages[index];
    item.shareOfVoiceLabel = `${item.shareOfVoiceValue}%`;
  });

  return summaryItems;
});

const noDataMessage = computed(() =>
  isEmpty(comparisonStore.comparisonFilters) ? NO_DATA_FOUND : NO_DATA_FOUND_FOR_FILTERS,
);

const sortedSummaryItems = computed(() =>
  orderBy(summaryStats.value, [sortField.value], [sortOrder.value?.toLowerCase()]),
);

function formatSentimentBadge(sentiment) {
  if (sentiment > 0) return 'positive';
  if (sentiment === 0) return 'neutral';
  return 'negative';
}
</script>

<template>
  <Section>
    <SectionHeader title="Comparison Summary" />
    <DataTable
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :value="sortedSummaryItems"
      :empty-state="{
        title: 'No data found',
        message: noDataMessage,
      }"
      :loading="comparisonStore.pendingDataPull.topicsStats"
      :num-loading-items="
        comparisonStore.selectedComparison?.topics?.length || TOPIC_COMPARISON_MAX_TOPIC_COUNT
      "
      removable-sort
    >
      <Column field="topic" header="Topic" sortable class="w-48" />
      <Column
        field="mentionsValue"
        header="Mentions"
        sortable
        :tooltip="TOPIC_COMPARISONS_TOOLTIPS.MENTIONS"
        class="w-48"
      >
        <template #body="{ data }">{{ data.mentionsLabel }}</template>
      </Column>
      <Column
        field="shareOfVoiceValue"
        header="Share of Voice"
        sortable
        :tooltip="TOPIC_COMPARISONS_TOOLTIPS.SHARE_OF_VOICE"
        class="w-48"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-1">
            <ProgressBar
              v-if="isNumber(data.shareOfVoiceValue)"
              class="w-30"
              :value="data.shareOfVoiceValue"
              severity="neutral"
            />
            <span>{{ data.shareOfVoiceLabel }}</span>
          </div>
        </template>
      </Column>
      <Column
        field="engagementsValue"
        header="Engagements"
        sortable
        :tooltip="TOPIC_COMPARISONS_TOOLTIPS.ENGAGEMENTS"
        class="w-48"
      >
        <template #body="{ data }">{{ data.engagementsLabel }}</template>
      </Column>
      <Column
        field="impressionsValue"
        header="Impressions"
        sortable
        :tooltip="TOPIC_COMPARISONS_TOOLTIPS.IMPRESSIONS"
        class="w-48"
      >
        <template #body="{ data }">{{ data.impressionsLabel }}</template>
      </Column>
      <Column
        field="netSentimentValue"
        header="Net Sentiment"
        sortable
        :tooltip="TOPIC_COMPARISONS_TOOLTIPS.NET_SENTIMENT"
        class="w-48"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-1">
            <Badge
              v-if="isNumber(data.netSentimentValue)"
              dot
              :severity="formatSentimentBadge(data.netSentimentValue)"
            />
            <span>{{ data.netSentimentLabel }}</span>
          </div>
        </template>
      </Column>
    </DataTable>
  </Section>
</template>
